import type { HotelOfferCardModel, HotelFilterState } from '../types/hotels.presentation';
import { matchMealCodes } from '../constants/hotelMasterData';

/**
 * Apply frontend-only filters to a list of hotel offer cards.
 * Returns a new filtered array — never mutates the input.
 */
export function applyHotelFilters(
  cards: HotelOfferCardModel[],
  filters: HotelFilterState,
): HotelOfferCardModel[] {
  return cards.filter((card) => {
    if (filters.name) {
      const nameLower = filters.name.toLocaleLowerCase('es-AR');
      if (!card.hotelName.toLocaleLowerCase('es-AR').includes(nameLower)) return false;
    }

    if (filters.priceMin && card.totalPrice < filters.priceMin) return false;
    if (filters.priceMax && card.totalPrice > filters.priceMax) return false;

    if (
      filters.starRatings !== undefined &&
      filters.starRatings.length > 0 &&
      !(card.stars !== undefined && filters.starRatings.includes(card.stars))
    ) {
      return false;
    }

    // Meals filter (OR): when availableMealTypes is present use exact
    // code matching; otherwise fall back to keyword heuristics on
    // rateFeature meal text (legacy data without mealType codes).
    if (filters.selectedMeals !== undefined && filters.selectedMeals.length > 0) {
      const hasMealMatch = mealCardMatchesFilter(card, filters.selectedMeals);
      if (!hasMealMatch) return false;
    }

    // Hotel type filter (OR): card must have hotelTypeId & it must match
    // one of the selected BM type ids.
    if (filters.selectedHotelTypes !== undefined && filters.selectedHotelTypes.length > 0) {
      if (card.hotelTypeId === undefined) return false;
      if (!filters.selectedHotelTypes.includes(card.hotelTypeId)) return false;
    }

    // Promotions filter (boolean): when enabled, only show cards with hasPromotion.
    if (filters.promotionsOnly) {
      if (!card.hasPromotion) return false;
    }

    return true;
  });
}

/**
 * Returns true when at least one selected meal code matches the card.
 *
 * Prefers exact matching against `availableMealTypes` (collected from
 * every rate); falls back to keyword heuristics on `rateFeatures` meal
 * text when no mealType codes are available (legacy data).
 */
function mealCardMatchesFilter(card: HotelOfferCardModel, selectedMeals: string[]): boolean {
  // Exact matching path
  if (card.availableMealTypes && card.availableMealTypes.length > 0) {
    return selectedMeals.some((code) => card.availableMealTypes.includes(code));
  }

  // Keyword-heuristic fallback
  const cardMealValues =
    card.rateFeatures?.filter((f) => f.type === 'meal').map((f) => f.value) ?? [];

  if (cardMealValues.length === 0) return false;

  return selectedMeals.some((code) =>
    cardMealValues.some((mealText) => matchMealCodes(mealText).includes(code)),
  );
}

/**
 * Collect all meal codes available on a card for discovery/counting.
 * Prefers exact codes from `availableMealTypes`; falls back to keyword
 * extraction from `rateFeatures` meal text.
 */
function cardMealCodes(card: HotelOfferCardModel): string[] {
  if (card.availableMealTypes && card.availableMealTypes.length > 0) {
    return card.availableMealTypes;
  }

  const mealValues = card.rateFeatures?.filter((f) => f.type === 'meal').map((f) => f.value) ?? [];

  const codes = new Set<string>();
  for (const mealValue of mealValues) {
    for (const code of matchMealCodes(mealValue)) {
      codes.add(code);
    }
  }
  return [...codes];
}

/**
 * Count how many cards match each meal option, after applying all active
 * filters EXCEPT the meals filter itself. This gives facet counts that show
 * how many hotels would remain if the user toggled a given meal option.
 *
 * Uses exact mealType codes from `availableMealTypes` when present;
 * falls back to keyword heuristics on `rateFeatures` meal text for legacy
 * data without mealType codes.
 *
 * Returns a Record mapping BM meal code → count.
 */
export function computeMealCounts(
  cards: HotelOfferCardModel[],
  filters: HotelFilterState,
): Record<string, number> {
  const baseFilters = { ...filters };
  delete baseFilters.selectedMeals;

  const baseCards = hasActiveFilters(baseFilters) ? applyHotelFilters(cards, baseFilters) : cards;

  // Discover all meal codes present anywhere in the pool (unfiltered).
  const allCodes = new Set<string>();
  for (const card of cards) {
    for (const code of cardMealCodes(card)) {
      allCodes.add(code);
    }
  }

  const counts: Record<string, number> = {};
  for (const code of allCodes) {
    counts[code] = 0;
  }
  for (const card of baseCards) {
    for (const code of cardMealCodes(card)) {
      counts[code] = (counts[code] || 0) + 1;
    }
  }
  return counts;
}

/**
 * Count how many cards match each hotel type option, after applying all
 * active filters EXCEPT the hotel type filter itself.
 *
 * Returns a Record mapping BM hotel type id → count.
 */
export function computeHotelTypeCounts(
  cards: HotelOfferCardModel[],
  filters: HotelFilterState,
): Record<number, number> {
  const baseFilters = { ...filters };
  delete baseFilters.selectedHotelTypes;

  const baseCards = hasActiveFilters(baseFilters) ? applyHotelFilters(cards, baseFilters) : cards;

  // Discover all hotel type IDs present anywhere in the pool (unfiltered).
  const allIds = new Set<number>();
  for (const card of cards) {
    if (card.hotelTypeId !== undefined) {
      allIds.add(card.hotelTypeId);
    }
  }

  const counts: Record<number, number> = {};
  for (const id of allIds) {
    counts[id] = 0;
  }
  for (const card of baseCards) {
    if (card.hotelTypeId !== undefined) {
      counts[card.hotelTypeId] = (counts[card.hotelTypeId] || 0) + 1;
    }
  }
  return counts;
}

/**
 * Returns true when at least one card in the list has a promotional rate.
 */
export function hasPromotionsAvailable(cards: HotelOfferCardModel[]): boolean {
  return cards.some((card) => card.hasPromotion === true);
}

/**
 * Sort a list of hotel offer cards by totalPrice.
 * Returns a new array — never mutates input.
 * Missing/invalid prices are ordered to the end.
 */
export function applyHotelSort(
  cards: readonly HotelOfferCardModel[],
  sortBy?: 'price-asc' | 'price-desc',
): HotelOfferCardModel[] {
  if (!sortBy) return [...cards];

  const multiplier = sortBy === 'price-asc' ? 1 : -1;

  return [...cards].sort((a, b) => {
    const aValid = typeof a.totalPrice === 'number' && !Number.isNaN(a.totalPrice);
    const bValid = typeof b.totalPrice === 'number' && !Number.isNaN(b.totalPrice);

    // Missing or invalid prices always sort to the end, regardless of direction.
    if (!aValid && !bValid) return 0;
    if (!aValid) return 1;
    if (!bValid) return -1;

    return (a.totalPrice - b.totalPrice) * multiplier;
  });
}

/**
 * Returns true when at least one filter field has a meaningful value.
 */
export function hasActiveFilters(filters: HotelFilterState): boolean {
  return (
    (filters.name !== undefined && filters.name.trim().length > 0) ||
    (filters.priceMin !== undefined && filters.priceMin > 0) ||
    (filters.priceMax !== undefined && filters.priceMax > 0) ||
    (filters.starRatings !== undefined && filters.starRatings.length > 0) ||
    (filters.selectedMeals !== undefined && filters.selectedMeals.length > 0) ||
    (filters.selectedHotelTypes !== undefined && filters.selectedHotelTypes.length > 0) ||
    filters.promotionsOnly === true
  );
}
